// @ts-check
// Página de uma automação (contrato 08, seção 7): nome, gatilho com as configurações do tipo, condições, ações e o
// histórico de execuções. Equivale ao editor de workflow do Twenty numa forma linear (um gatilho, ações em ordem).
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { PageCard, PageHeader } from '../page/page-card.js';
import { SettingsSection } from '../settings/settings-pages.js';
import { hasCurrentPermissionFlag, objectsByNameSingular, objectsState } from '../../data/metadata-store.js';
import { loadWorkspaceMembers, workspaceMembersState } from '../../data/workspace-members.js';
import { createAutomation, deleteAutomation, getAutomation, updateAutomation } from '../../data/automations.js';
import { ApiError, getErrorMessage } from '../../data/api-client.js';
import { Link, navigate } from '../../app/router.js';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { Skeleton, Toggle } from '../../ui/feedback.js';
import { Modal } from '../../ui/modal.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { NotFoundPage } from '../../app/not-found-page.js';
import { AutomationActionsEditor, createDefaultAction } from './automation-actions-editor.js';
import { DraftBanner, VersionHistory, WebhookPanel } from './automation-versions-panel.js';
import { buildVariableGroups } from './automation-variables.js';
import { ConditionsEditor, parseConditions, serializeConditions } from './automation-conditions.js';
import { AutomationRunList } from './automation-runs.js';
import { invalidateManualAutomations } from './manual-automations.js';
import {
  TRIGGER_ICONS, TRIGGER_TYPES, automationObjects, isRecordlessTrigger, isScheduleDateField, isWatchableField, pickFields, recordKeyOf,
} from './automation-labels.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/automations.js').Automation} Automation
 * @typedef {import('../../data/automations.js').AutomationAction} AutomationAction
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {{ name: string, description: string, objectNameSingular: string, triggerType: string, triggerSettings: Record<string, any>,
 *   conditionRows: import('./automation-conditions.js').ConditionRow[] | null, rawConditions: any, actions: AutomationAction[], isEnabled: boolean }} Draft
 */

const DEFAULT_OBJECT = 'enrollmentOpportunity';
const EDITOR_FORM_ID = 'automation-editor-form';

/** A tela edita o rascunho quando há um; senão, a versão publicada. @param {Automation} automation @returns {Draft} */
function toDraft(automation) {
  const definition = automation.draft?.definition ?? automation;
  return {
    name: automation.name, description: automation.description ?? '', objectNameSingular: definition.objectNameSingular,
    triggerType: definition.triggerType, triggerSettings: { ...definition.triggerSettings },
    conditionRows: parseConditions(definition.conditions), rawConditions: definition.conditions,
    actions: structuredClone(definition.actions), isEnabled: automation.isEnabled,
  };
}

/** Ação para a API: sem os marcadores de segredo que só a API devolve. @param {AutomationAction} action @returns {AutomationAction} */
function toActionPayload(action) {
  const { hasSecret: _hasSecret, ...rest } = action;
  if (rest.type === 'CALL_WEBHOOK' && !rest.secret) delete rest.secret;
  if (rest.type === 'HTTP_REQUEST') rest.headers = (rest.headers ?? []).map((/** @type {any} */ { hasValue: _hasValue, ...header }) => header);
  if (rest.type === 'IF_ELSE') rest.branches = { yes: (rest.branches?.yes ?? []).map(toActionPayload), no: (rest.branches?.no ?? []).map(toActionPayload) };
  return rest;
}

/** @returns {Draft} */
function emptyDraft() {
  const objectNameSingular = objectsByNameSingular.value.has(DEFAULT_OBJECT) ? DEFAULT_OBJECT : automationObjects(objectsState.value)[0]?.nameSingular ?? '';
  return {
    name: '', description: '', objectNameSingular, triggerType: 'RECORD_CREATED', triggerSettings: {}, conditionRows: [], rawConditions: null,
    actions: [createDefaultAction('SEND_NOTIFICATION')], isEnabled: false,
  };
}

/** Configuração inicial de cada gatilho para o objeto escolhido. @param {string} triggerType @param {ObjectMetadata | undefined} objectMetadata */
function defaultTriggerSettings(triggerType, objectMetadata) {
  const dateFields = pickFields(objectMetadata, isScheduleDateField);
  switch (triggerType) {
    case 'FIELD_CHANGED':
      return { fieldNames: [] };
    case 'STAGE_CHANGED': {
      const selectField = objectMetadata?.fields.find((field) => field.name === 'stage' && field.type === 'SELECT') ?? pickFields(objectMetadata, (field) => field.type === 'SELECT')[0];
      return { fieldName: selectField?.name ?? 'stage', toValues: [], fromValues: [] };
    }
    case 'DATE_APPROACHING':
      return { fieldName: dateFields[0]?.name ?? '', amount: 24, unit: 'HOURS' };
    case 'RECORD_STALE':
      return { referenceFieldName: objectMetadata?.fields.some((field) => field.name === 'stageChangedAt') ? 'stageChangedAt' : 'updatedAt', days: 7 };
    case 'SCHEDULED':
      return { frequency: 'DAILY', time: '08:00', weekday: null, dayOfMonth: null, mode: 'RECORDS', maxRecords: 200 };
    case 'WEBHOOK':
      return { authentication: 'SECRET', expectedBody: null };
    default:
      return {};
  }
}

/** @param {Draft} draft @returns {Partial<Automation>} */
function toPayload(draft) {
  return {
    name: draft.name.trim(), description: draft.description.trim() || null, objectNameSingular: draft.objectNameSingular,
    triggerType: /** @type {Automation['triggerType']} */ (draft.triggerType), triggerSettings: draft.triggerSettings,
    conditions: draft.conditionRows === null ? draft.rawConditions : serializeConditions(draft.conditionRows),
    // segredo vazio mantém o salvo (a API nunca devolve o segredo)
    actions: draft.actions.map(toActionPayload),
    isEnabled: draft.isEnabled,
  };
}

/**
 * @param {{ values: Array<{ value: string, label: string }>, selected: string[], disabled: boolean, onChange: (values: string[]) => void }} props
 */
function ChipPicker({ values, selected, disabled, onChange }) {
  const selectedSet = new Set(selected);
  return html`<div class="automation-chip-picker">
    ${values.map((item) => html`<button type="button" key=${item.value} class="automation-chip" aria-pressed=${selectedSet.has(item.value) ? 'true' : 'false'} disabled=${disabled}
      onClick=${() => onChange(selectedSet.has(item.value) ? selected.filter((value) => value !== item.value) : [...selected, item.value])}>${item.label}</button>`)}
  </div>`;
}

/**
 * @param {{ draft: Draft, objectMetadata: ObjectMetadata | undefined, disabled: boolean, onChange: (patch: Partial<Draft>) => void }} props
 */
function TriggerSettingsFields({ draft, objectMetadata, disabled, onChange }) {
  const settings = draft.triggerSettings;
  const setSettings = (/** @type {Record<string, any>} */ patch) => onChange({ triggerSettings: { ...settings, ...patch } });
  const dateFields = pickFields(objectMetadata, isScheduleDateField);
  const referenceFields = pickFields(objectMetadata, (field) => field.type === 'DATE_TIME' || field.type === 'DATE');
  const label = (/** @type {string} */ key) => html`<span class="form-field-label">${t(key)}</span>`;
  switch (draft.triggerType) {
    case 'FIELD_CHANGED': {
      const fields = pickFields(objectMetadata, isWatchableField);
      return html`<div class="form-field">${label('automations.form.watchedFields')}
        <${ChipPicker} values=${fields.map((field) => ({ value: field.name, label: translateMetadataLabel(field.label) }))} selected=${settings.fieldNames ?? []} disabled=${disabled}
          onChange=${(fieldNames) => setSettings({ fieldNames })} />
      </div>`;
    }
    case 'STAGE_CHANGED': {
      const selectFields = pickFields(objectMetadata, (field) => field.type === 'SELECT');
      const field = selectFields.find((candidate) => candidate.name === settings.fieldName);
      const options = (field?.options ?? []).map((option) => ({ value: option.value, label: translateMetadataLabel(option.label) }));
      return html`
        <label class="form-field">${label('automations.form.stageField')}
          <select class="text-input" value=${settings.fieldName ?? ''} disabled=${disabled} onChange=${(event) => setSettings({ fieldName: event.currentTarget.value, toValues: [], fromValues: [] })}>
            ${selectFields.map((candidate) => html`<option key=${candidate.id} value=${candidate.name}>${translateMetadataLabel(candidate.label)}</option>`)}
          </select>
        </label>
        <div class="form-field">${label('automations.form.toValues')}
          <${ChipPicker} values=${options} selected=${settings.toValues ?? []} disabled=${disabled} onChange=${(toValues) => setSettings({ toValues })} />
          <span class="input-hint">${t('automations.form.anyValueHint')}</span>
        </div>
        <div class="form-field">${label('automations.form.fromValues')}
          <${ChipPicker} values=${options} selected=${settings.fromValues ?? []} disabled=${disabled} onChange=${(fromValues) => setSettings({ fromValues })} />
        </div>`;
    }
    case 'DATE_APPROACHING':
      return html`<div class="form-field-row automation-due-row">
        <label class="form-field">${label('automations.form.amount')}
          <input class="text-input" type="number" min="1" max="365" value=${settings.amount ?? 24} disabled=${disabled} onInput=${(event) => setSettings({ amount: Number(event.currentTarget.value) })} />
        </label>
        <label class="form-field">${label('automations.form.unit')}
          <select class="text-input" value=${settings.unit ?? 'HOURS'} disabled=${disabled} onChange=${(event) => setSettings({ unit: event.currentTarget.value })}>
            ${['HOURS', 'DAYS'].map((unit) => html`<option key=${unit} value=${unit}>${t(`automations.units.${unit}`, { count: 2 })}</option>`)}
          </select>
        </label>
        <label class="form-field">${label('automations.form.beforeField')}
          <select class="text-input" value=${settings.fieldName ?? ''} disabled=${disabled} onChange=${(event) => setSettings({ fieldName: event.currentTarget.value })}>
            ${dateFields.map((field) => html`<option key=${field.id} value=${field.name}>${translateMetadataLabel(field.label)}</option>`)}
          </select>
        </label>
      </div>`;
    case 'SCHEDULED':
      return html`<div class="form-field-row">
          <label class="form-field">${label('automations.schedule.frequency')}
            <select class="text-input" value=${settings.frequency ?? 'DAILY'} disabled=${disabled} data-testid="schedule-frequency"
              onChange=${(event) => setSettings({ frequency: event.currentTarget.value, weekday: event.currentTarget.value === 'WEEKLY' ? settings.weekday ?? 1 : null, dayOfMonth: event.currentTarget.value === 'MONTHLY' ? settings.dayOfMonth ?? 1 : null })}>
              ${['DAILY', 'WEEKLY', 'MONTHLY'].map((frequency) => html`<option key=${frequency} value=${frequency}>${t(`automations.schedule.frequencies.${frequency}`)}</option>`)}
            </select>
          </label>
          ${settings.frequency === 'WEEKLY'
            ? html`<label class="form-field">${label('automations.schedule.weekday')}
                <select class="text-input" value=${settings.weekday ?? 1} disabled=${disabled} onChange=${(event) => setSettings({ weekday: Number(event.currentTarget.value) })}>
                  ${[1, 2, 3, 4, 5, 6, 0].map((weekday) => html`<option key=${weekday} value=${weekday}>${t(`automations.schedule.weekdays.${weekday}`)}</option>`)}
                </select>
              </label>`
            : null}
          ${settings.frequency === 'MONTHLY'
            ? html`<label class="form-field">${label('automations.schedule.dayOfMonth')}
                <input class="text-input" type="number" min="1" max="31" value=${settings.dayOfMonth ?? 1} disabled=${disabled} onInput=${(event) => setSettings({ dayOfMonth: Number(event.currentTarget.value) })} />
              </label>`
            : null}
          <label class="form-field">${label('automations.schedule.time')}
            <input class="text-input" type="time" value=${settings.time ?? '08:00'} disabled=${disabled} onInput=${(event) => setSettings({ time: event.currentTarget.value })} />
          </label>
        </div>
        <span class="input-hint">${t('automations.schedule.timeZoneHint')}</span>
        <div class="form-field-row">
          <label class="form-field">${label('automations.schedule.mode')}
            <select class="text-input" value=${settings.mode ?? 'RECORDS'} disabled=${disabled} data-testid="schedule-mode"
              onChange=${(event) => setSettings({ mode: event.currentTarget.value, maxRecords: event.currentTarget.value === 'RECORDS' ? settings.maxRecords ?? 200 : null })}>
              ${['RECORDS', 'ONCE'].map((mode) => html`<option key=${mode} value=${mode}>${t(`automations.schedule.modes.${mode}`, { object: objectMetadata ? translateMetadataLabel(objectMetadata.labelPlural) : '' })}</option>`)}
            </select>
          </label>
          ${settings.mode !== 'ONCE'
            ? html`<label class="form-field">${label('automations.schedule.maxRecords')}
                <input class="text-input" type="number" min="1" max="1000" value=${settings.maxRecords ?? 200} disabled=${disabled} onInput=${(event) => setSettings({ maxRecords: Number(event.currentTarget.value) })} />
              </label>`
            : html`<span></span>`}
        </div>
        <span class="input-hint">${settings.mode === 'ONCE' ? t('automations.schedule.onceHint') : t('automations.schedule.recordsHint')}</span>`;
    case 'WEBHOOK':
      return html`<label class="form-field">${label('automations.webhook.authentication')}
          <select class="text-input" value=${settings.authentication ?? 'SECRET'} disabled=${disabled} onChange=${(event) => setSettings({ authentication: event.currentTarget.value })}>
            ${['SECRET', 'API_KEY'].map((value) => html`<option key=${value} value=${value}>${t(`automations.webhook.authentications.${value}`)}</option>`)}
          </select>
        </label>
        <label class="form-field">${label('automations.webhook.expectedBody')}
          <textarea class="text-input automation-textarea" rows="4" value=${settings.expectedBody ?? ''} disabled=${disabled} placeholder='{"nome": "Maria Silva", "email": "maria@email.com"}'
            data-testid="webhook-expected-body" onInput=${(event) => setSettings({ expectedBody: event.currentTarget.value || null })}></textarea>
          <span class="input-hint">${t('automations.webhook.expectedBodyHint')}</span>
        </label>`;
    case 'RECORD_STALE':
      return html`<div class="form-field-row">
        <label class="form-field">${label('automations.form.staleDays')}
          <input class="text-input" type="number" min="1" max="365" value=${settings.days ?? 7} disabled=${disabled} onInput=${(event) => setSettings({ days: Number(event.currentTarget.value) })} />
        </label>
        <label class="form-field">${label('automations.form.referenceField')}
          <select class="text-input" value=${settings.referenceFieldName ?? ''} disabled=${disabled} onChange=${(event) => setSettings({ referenceFieldName: event.currentTarget.value })}>
            ${referenceFields.map((field) => html`<option key=${field.id} value=${field.name}>${translateMetadataLabel(field.label)}</option>`)}
          </select>
        </label>
      </div>`;
    default:
      return null;
  }
}

/** @param {{ objectMetadata: ObjectMetadata | undefined }} props */
function VariablesHelp({ objectMetadata }) {
  const fields = pickFields(objectMetadata, (field) => isWatchableField(field) && field.type !== 'RAW_JSON' && field.type !== 'FILES');
  return html`<details class="automation-variables">
    <summary>${t('automations.form.variablesTitle')}</summary>
    <p class="automation-muted">${t('automations.form.variablesDescription')}</p>
    <ul>
      <li><code>{{recordUrl}}</code> ${t('automations.form.variableRecordUrl')}</li>
      <li><code>{{institution.name}}</code> ${t('automations.form.variableInstitution')}</li>
      <li><code>${'{{contact.<campo>}}'}</code> ${t('automations.form.variableContact')}</li>
      ${fields.map((field) => html`<li key=${field.id}><code>${`{{record.${field.type === 'RELATION' ? field.name : recordKeyOf(field)}}}`}</code> ${translateMetadataLabel(field.label)}</li>`)}
    </ul>
  </details>`;
}

/**
 * @param {{ automationId: string }} props
 */
export function AutomationEditorPage({ automationId }) {
  const isNew = automationId === 'new';
  // a flag WORKFLOWS do papel decide quem altera automações (a API responde 403 aos demais)
  const isAdmin = hasCurrentPermissionFlag('WORKFLOWS');
  const [automation, setAutomation] = useState(/** @type {Automation | null} */ (null));
  const [draft, setDraft] = useState(/** @type {Draft | null} */ (isNew ? emptyDraft() : null));
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [runsKey, setRunsKey] = useState(0);
  const members = workspaceMembersState.value ?? [];

  useEffect(() => {
    loadWorkspaceMembers();
    if (isNew) {
      document.title = `${t('automations.new')} - ${t('navigation.settings')}`;
      return;
    }
    getAutomation(automationId)
      .then((loaded) => {
        setAutomation(loaded);
        setDraft(toDraft(loaded));
        document.title = `${loaded.name} - ${t('automations.title')}`;
      })
      .catch(() => setLoadFailed(true));
  }, [automationId]);

  if (loadFailed) return html`<${NotFoundPage} />`;
  const objectMetadata = draft ? objectsByNameSingular.value.get(draft.objectNameSingular) : undefined;
  const disabled = !isAdmin;
  const update = (/** @type {Partial<Draft>} */ patch) => setDraft((current) => (current ? { ...current, ...patch } : current));
  /** @param {Automation} saved */
  const applySaved = (saved) => {
    setAutomation(saved);
    setDraft(toDraft(saved));
    setRunsKey((key) => key + 1);
  };

  const save = async (/** @type {Event} */ event) => {
    event.preventDefault();
    if (!draft) return;
    setError(null);
    setIsSaving(true);
    try {
      const payload = toPayload(draft);
      const saved = isNew ? await createAutomation(payload) : await updateAutomation(automationId, payload);
      invalidateManualAutomations();
      // automação ativa: a mudança fica no rascunho até publicar
      showSuccessSnackbar(saved.draft ? t('automations.versions.draftSavedSnackbar') : t('automations.savedSnackbar'));
      applySaved(saved);
      if (isNew) navigate(`/settings/automations/${saved.id}`, { replace: true });
    } catch (requestError) {
      const detail = requestError instanceof ApiError ? requestError.messages[0] : null;
      setError(detail ?? getErrorMessage(requestError));
    } finally {
      setIsSaving(false);
    }
  };

  const remove = async () => {
    try {
      await deleteAutomation(automationId);
      showSuccessSnackbar(t('automations.deletedSnackbar'));
      navigate('/settings/automations');
    } catch (requestError) {
      showErrorSnackbar(requestError);
    }
  };

  const breadcrumb = html`<nav class="record-breadcrumb">
    <${Link} href="/settings/automations" className="record-breadcrumb-prefix">${t('automations.title')}<//>
    <span class="record-breadcrumb-separator">/</span>
    <span class="record-breadcrumb-current">${isNew ? t('automations.new') : automation?.name ?? ''}</span>
  </nav>`;

  if (!draft) {
    return html`<${PageCard} header=${html`<${PageHeader} breadcrumb=${breadcrumb} />`}>
      <div class="settings-page"><${Skeleton} height=${120} radius=${8} /><${Skeleton} height=${200} radius=${8} /></div>
    <//>`;
  }

  const objects = automationObjects(objectsState.value);
  // salvar e cancelar no cabeçalho, como nas configurações do Twenty
  const headerActions = isAdmin
    ? html`<${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${() => navigate('/settings/automations')} />
        <${Button} title=${automation?.isEnabled && draft.isEnabled ? t('automations.versions.saveDraft') : t('common.save')} icon="IconDeviceFloppy" accent="blue" size="small"
          type="submit" form=${EDITOR_FORM_ID} isLoading=${isSaving} />`
    : null;
  const recordless = isRecordlessTrigger(draft.triggerType, draft.triggerSettings);
  const scope = { objectMetadata, triggerType: draft.triggerType, triggerSettings: draft.triggerSettings, actions: draft.actions };
  const conditionGroups = draft.triggerType === 'WEBHOOK' ? buildVariableGroups(scope, null) : undefined;
  return html`<${PageCard} header=${html`<${PageHeader} breadcrumb=${breadcrumb} actions=${headerActions} />`}>
    <form id=${EDITOR_FORM_ID} class="settings-page automation-editor" onSubmit=${save}>
      ${disabled ? html`<p class="channel-card-note"><${Icon} name="IconLock" size=${14} />${t('automations.adminOnly')}</p>` : null}
      ${automation?.isPackage ? html`<p class="automation-package-note"><${Icon} name="IconInfoCircle" size=${14} />${t('automations.packageNote')}</p>` : null}
      ${automation ? html`<${DraftBanner} automation=${automation} isAdmin=${isAdmin} onChanged=${applySaved} />` : null}
      <${SettingsSection} title=${t('automations.form.generalTitle')}>
        <label class="form-field">
          <span class="form-field-label">${t('automations.form.name')}</span>
          <input class="text-input" value=${draft.name} disabled=${disabled} maxlength="120" required onInput=${(event) => update({ name: event.currentTarget.value })} />
        </label>
        <label class="form-field">
          <span class="form-field-label">${t('automations.form.description')}</span>
          <textarea class="text-input automation-textarea" rows="2" value=${draft.description} disabled=${disabled} onInput=${(event) => update({ description: event.currentTarget.value })}></textarea>
        </label>
        <label class="automation-inline-toggle">
          <${Toggle} value=${draft.isEnabled} onChange=${disabled ? undefined : (isEnabled) => update({ isEnabled })} ariaLabel=${t('automations.form.enabled')} />
          <span>${draft.isEnabled ? t('automations.form.enabledOn') : t('automations.form.enabledOff')}</span>
        </label>
      <//>
      <${SettingsSection} title=${t('automations.form.triggerTitle')} description=${t('automations.form.triggerDescription')}>
        <div class="form-field-row">
          <label class="form-field">
            <span class="form-field-label">${t('automations.form.object')}</span>
            <select class="text-input" value=${draft.objectNameSingular} disabled=${disabled || !isNew} onChange=${(event) => {
              const next = objectsByNameSingular.value.get(event.currentTarget.value);
              update({ objectNameSingular: event.currentTarget.value, triggerSettings: defaultTriggerSettings(draft.triggerType, next), conditionRows: [], rawConditions: null });
            }}>
              ${objects.map((candidate) => html`<option key=${candidate.id} value=${candidate.nameSingular}>${translateMetadataLabel(candidate.labelSingular)}</option>`)}
            </select>
          </label>
          <label class="form-field">
            <span class="form-field-label">${t('automations.form.triggerType')}</span>
            <select class="text-input" value=${draft.triggerType} disabled=${disabled} data-testid="trigger-type" onChange=${(event) => {
              const triggerType = event.currentTarget.value;
              const triggerSettings = defaultTriggerSettings(triggerType, objectMetadata);
              // sem registro, as condições sobre campos deixam de valer
              const resetConditions = isRecordlessTrigger(triggerType, triggerSettings) !== recordless ? { conditionRows: [], rawConditions: null } : {};
              update({ triggerType, triggerSettings, ...resetConditions });
            }}>
              ${TRIGGER_TYPES.map((type) => html`<option key=${type} value=${type}>${t(`automations.triggerTypes.${type}`)}</option>`)}
            </select>
          </label>
        </div>
        <p class="automation-trigger-help"><${Icon} name=${TRIGGER_ICONS[/** @type {keyof typeof TRIGGER_ICONS} */ (draft.triggerType)]} size=${14} />${t(`automations.triggerHelp.${draft.triggerType}`)}</p>
        <${TriggerSettingsFields} draft=${draft} objectMetadata=${objectMetadata} disabled=${disabled} onChange=${update} />
      <//>
      ${draft.triggerType === 'SCHEDULED' && draft.triggerSettings.mode === 'ONCE'
        ? null
        : html`<${SettingsSection} title=${t('automations.form.conditionsTitle')}
            description=${draft.triggerType === 'WEBHOOK' ? t('automations.webhook.conditionsDescription') : draft.triggerType === 'SCHEDULED' ? t('automations.schedule.conditionsDescription') : t('automations.form.conditionsDescription')}>
            <${ConditionsEditor} objectMetadata=${recordless ? undefined : objectMetadata} rows=${draft.conditionRows} rawConditions=${draft.rawConditions} members=${members} disabled=${disabled}
              variableGroups=${conditionGroups} allowVariableFields=${draft.triggerType === 'WEBHOOK'} onChange=${(conditionRows) => update({ conditionRows, rawConditions: null })} />
          <//>`}
      <${SettingsSection} title=${t('automations.form.actionsTitle')} description=${t('automations.form.actionsDescription')}>
        <${AutomationActionsEditor} actions=${draft.actions} objectMetadata=${objectMetadata} members=${members} disabled=${disabled} triggerType=${draft.triggerType}
          triggerSettings=${draft.triggerSettings} onChange=${(actions) => update({ actions })} />
        ${recordless ? null : html`<${VariablesHelp} objectMetadata=${objectMetadata} />`}
      <//>
      ${error ? html`<div class="form-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${error}</span></div>` : null}
      ${automation?.webhook
        ? html`<${SettingsSection} title=${t('automations.webhook.title')} description=${t('automations.webhook.description')}>
            <${WebhookPanel} automation=${automation} isAdmin=${isAdmin} onChanged=${applySaved} />
          <//>`
        : null}
      ${automation
        ? html`<${SettingsSection} title=${t('automations.versions.title')} description=${t('automations.versions.description')}>
            <${VersionHistory} automation=${automation} isAdmin=${isAdmin} refreshKey=${runsKey} onChanged=${applySaved} />
          <//>`
        : null}
      ${!isNew
        ? html`<${SettingsSection} title=${t('automations.runs.title')} description=${t('automations.runs.descriptionSingle')}>
            <${AutomationRunList} automationId=${automationId} isAdmin=${isAdmin} refreshKey=${runsKey} />
          <//>`
        : null}
      ${isAdmin && !isNew
        ? html`<${SettingsSection} title=${t('automations.dangerZone')} description=${t('automations.deleteDescription')}>
            <div><${Button} title=${t('automations.delete')} icon="IconTrash" variant="secondary" accent="danger" size="small" onClick=${() => setIsConfirmingDelete(true)} /></div>
          <//>`
        : null}
    </form>
    ${isConfirmingDelete
      ? html`<${Modal} title=${t('automations.deleteTitle')} subtitle=${t('automations.deleteSubtitle', { name: automation?.name ?? '' })}
          confirmLabel=${t('automations.delete')} confirmAccent="danger" onClose=${() => setIsConfirmingDelete(false)} onConfirm=${remove} />`
      : null}
  <//>`;
}
